import { useState } from 'react';

/**
 * InBody Tooltip — two-level tooltip with ? icon.
 * Click to open short text. Click "Saber mais" to expand full text.
 */
export function InBodyTooltip({ short, expanded, className = '' }) {
  const [open, setOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);

  return (
    <span className={`relative inline-flex ${className}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); setShowMore(false); }}
        className="w-4 h-4 rounded-full bg-brand-bg border border-brand-border text-[9px] text-brand-muted flex items-center justify-center hover:bg-brand-green/10 hover:text-brand-green hover:border-brand-green/30 transition-all ml-1"
        data-testid="tooltip-trigger"
      >?</button>
      {open && (
        <div className="absolute z-50 bottom-6 left-0 w-64 bg-white border border-brand-border rounded-xl shadow-lg p-3 text-left" onClick={e => e.stopPropagation()}>
          <p className="text-xs text-brand-text leading-relaxed">{short}</p>
          {expanded && !showMore && (
            <button onClick={() => setShowMore(true)} className="text-[10px] text-brand-green mt-1.5 hover:underline">Saber mais</button>
          )}
          {expanded && showMore && (
            <p className="text-[11px] text-brand-muted leading-relaxed mt-2 pt-2 border-t border-brand-border/50">{expanded}</p>
          )}
          <button onClick={() => setOpen(false)} className="absolute top-1.5 right-2 text-brand-placeholder hover:text-brand-text text-xs">×</button>
        </div>
      )}
    </span>
  );
}

/**
 * Nutri-level tooltip — single level with technical content.
 */
export function NutriTooltip({ text, className = '' }) {
  const [open, setOpen] = useState(false);

  return (
    <span className={`relative inline-flex ${className}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="w-4 h-4 rounded-full bg-brand-green/10 border border-brand-green/20 text-[9px] text-brand-green flex items-center justify-center hover:bg-brand-green/20 transition-all ml-1"
        data-testid="nutri-tooltip-trigger"
      >?</button>
      {open && (
        <div className="absolute z-50 bottom-6 left-0 w-72 bg-white border border-brand-green/20 rounded-xl shadow-lg p-3 text-left" onClick={e => e.stopPropagation()}>
          <p className="text-[11px] text-brand-text leading-relaxed whitespace-pre-line">{text}</p>
          <button onClick={() => setOpen(false)} className="absolute top-1.5 right-2 text-brand-placeholder hover:text-brand-text text-xs">×</button>
        </div>
      )}
    </span>
  );
}

// Tooltip content data
export const CLIENT_TOOLTIPS = {
  inbody_score: {
    short: "Uma pontuação geral da tua composição corporal. Quanto mais alto, melhor o equilíbrio entre músculo e gordura.",
    expanded: "Esta pontuação resume a tua composição corporal numa escala de 0 a 100. Tem em conta a quantidade de músculo e gordura em relação ao teu peso ideal. Acima de 80 é excelente. Entre 70 e 80 há margem para melhorar."
  },
  massa_gorda: {
    short: "É a quantidade total de gordura no teu corpo, em kg e em percentagem do teu peso.",
    expanded: "A gordura corporal é necessária para a saúde, mas em excesso aumenta o risco de doenças. O valor saudável para mulheres situa-se entre 18% e 28% do peso total. O teu plano alimentar foi desenhado para trabalhar este equilíbrio de forma gradual e sustentada."
  },
  massa_muscular: {
    short: "É a quantidade de músculo esquelético no teu corpo. Quanto mais músculo, melhor o teu metabolismo e a tua força.",
    expanded: "O músculo esquelético é o músculo que move o teu corpo e que está ligado aos ossos. Ter boa massa muscular ajuda a manter o metabolismo ativo, a ter mais energia no dia a dia e a proteger as articulações."
  },
  agua: {
    short: "Quantidade total de água no teu corpo. Reflete o teu estado de hidratação geral.",
    expanded: "A água corporal total inclui a água dentro e fora das células. Em mulheres saudáveis, representa normalmente entre 45% e 60% do peso corporal. Uma boa hidratação é essencial para o funcionamento de todos os órgãos."
  },
  imc: {
    short: "Relaciona o teu peso com a tua altura. É um indicador de referência, mas não distingue gordura de músculo.",
    expanded: "O IMC é calculado dividindo o teu peso pelo quadrado da tua altura. Serve como orientação geral, mas tem limitações importantes: uma pessoa com bastante músculo pode ter IMC elevado sem excesso de gordura."
  },
  gordura_visceral: {
    short: "É a gordura que fica em volta dos órgãos internos. Níveis elevados aumentam o risco de doenças mesmo que o peso pareça normal.",
    expanded: "A gordura visceral acumula-se na zona abdominal, à volta do fígado, estômago e intestinos. O InBody apresenta o nível de 1 a 20. Até 9 é considerado bom. Acima de 13 é zona de risco aumentado."
  },
  grau_obesidade: {
    short: "Mostra em quanto o teu peso atual se afasta do teu peso ideal, calculado com base na tua altura.",
    expanded: "Este valor é expresso em percentagem. 100% significa que estás exatamente no peso ideal. Acima de 120% indica excesso de peso. Abaixo de 80% indica peso abaixo do ideal."
  },
  smi: {
    short: "Mede a quantidade de músculo nos braços e pernas em relação à tua altura. É o principal indicador para detetar perda de massa muscular.",
    expanded: "O SMI compara a tua massa muscular apendicular (braços e pernas) com o que seria esperado para a tua estatura. Manter um bom SMI é especialmente importante a partir dos 30 anos."
  },
  ffmi: {
    short: "Mede a tua massa magra total em relação à altura. Permite perceber se ganhou músculo ou apenas peso ao longo do tempo.",
    expanded: "O FFMI inclui músculo, osso e água, excluindo apenas a gordura. Se o FFMI subiu entre avaliações, ganhaste massa magra."
  },
  fmi: {
    short: "Mede a quantidade de gordura em relação à tua altura. É mais preciso do que o IMC para avaliar o excesso de gordura.",
    expanded: "Ao contrário do IMC, o FMI mede especificamente a gordura e não o peso total. Um FMI elevado, mesmo com peso aparentemente normal, pode indicar excesso de gordura que o IMC não detetaria."
  },
  racio_musculo_peso: {
    short: "Percentagem do teu peso total que é músculo. Quanto mais alto, melhor a tua composição corporal.",
    expanded: "Este rácio diz-te que parte do teu peso é músculo útil. Mesmo que o peso não mude muito, ver o rácio a subir significa que a tua composição está a melhorar."
  },
  circunferencia_abdominal: {
    short: "Mede o perímetro da zona abdominal. Reflete a acumulação de gordura visceral e o risco cardiovascular associado.",
    expanded: "A circunferência abdominal é medida acima do umbigo. Valores de referência: mulheres < 80 cm sem risco, 80-88 cm risco aumentado, > 88 cm risco muito aumentado."
  },
};

export const NUTRI_TOOLTIPS = {
  inbody_score: "Pontuação composta 0-100. Cada kg de músculo acima do ideal +1 pt; cada kg de gordura acima do ideal -1 pt. >80: excelente. 70-80: adequado. <70: atenção à composição.",
  massa_gorda: "Soma de toda a gordura corporal (subcutânea, visceral e intramuscular), em kg e %PGC. Saudável: 18-28% mulheres, 10-20% homens. >30% (M) ou >25% (H): risco metabólico aumentado.",
  massa_muscular: "Massa muscular esquelética total (MME) em kg. Inclui apenas músculo ligado ao esqueleto. Base para cálculo do SMI e FFMI. Valores de referência dependem de altura, sexo e idade.",
  agua: "ACT = AIC + AEC. Adultos saudáveis: 50-60% do peso. Rácio AEC/ACT normal: 0,36-0,39. >0,39 sugere retenção hídrica ou inflamação.",
  imc: "IMC = peso/altura² (kg/m²). OMS: <18,5 baixo peso | 18,5-24,9 normal | 25-29,9 excesso | 30-34,9 obesidade I | 35-39,9 obesidade II | ≥40 obesidade III. Não distingue massa gorda de magra.",
  gordura_visceral: "Nível 1-20. ≤9: saudável. 10-13: atenção. ≥14: risco elevado de síndrome metabólica, resistência à insulina e DCV. Metabolicamente ativa e inflamatória, impacto independente do IMC.",
  grau_obesidade: "% do peso atual vs peso ideal (método IMC padrão). 100% = ideal. <80%: baixo peso. 80-120%: normal. >120%: excesso. Nota: elevada MME pode dar >100% sem excesso de gordura.",
  smi: "SMI = massa muscular apendicular / altura² (kg/m²). Rastreio sarcopenia (EWGSOP2/AWGS): Normal ≥5,7 (M) / ≥7,0 (H). <5,7/<7,0: sarcopenia possível. Confirmar com força preensão.",
  ffmi: "FFMI = massa livre de gordura / altura² (kg/m²). Mediana adultos caucasianos: 18,9 (H), 15,4 (M). Baixo: <14 (M) / <17 (H). Normal: 14-17 (M) / 17-20 (H). Elevado: >17 (M) / >20 (H).",
  fmi: "FMI = massa gorda / altura² (kg/m²). Mediana: 4,0 (H), 5,5 (M). Risco cardiometabólico: >6,3-7,5 (H), >9,5-10,8 (M). Baixo: <2,5 (H) / <3,5 (M). Normal: 2,5-6 (H) / 3,5-8 (M). Elevado: >6 (H) / >8 (M).",
  racio_musculo_peso: "% do peso total composta por MME. Sem ponto de corte universal. Valores progressivamente mais elevados = melhoria da composição. Útil para monitorizar recomposição corporal.",
  circunferencia_abdominal: "Indicador indireto de adiposidade visceral. OMS/Europa: Sem risco <80 cm (M) / <94 cm (H). Risco aumentado 80-88 (M) / 94-102 (H). Risco muito aumentado >88 (M) / >102 (H).",
};
